import React, { useCallback, useEffect, useRef, useState } from "react"

import { fromEvent, Subscription } from "rxjs"
import { debounceTime } from "rxjs/operators"

interface HorizontalScrollWrapperProps {
  gridRef: React.RefObject<HTMLTableElement | HTMLDivElement>
  recalculateWidth: boolean
  visible?: boolean
  children: React.ReactNode
  offsetVertical?: number
}

function HorizontalScrollWrapper(props: HorizontalScrollWrapperProps) {
  const { gridRef, recalculateWidth, visible, children, offsetVertical } = props
  const [gridWidth, setGridWidth] = useState(0)

  const scrollWrapper = useRef<HTMLDivElement>(null)
  const gridWrapper = useRef<HTMLDivElement>(null)

  const updateWidth = useCallback(() => {
    if (gridRef.current && gridWrapper.current) {
      const { clientWidth, offsetWidth } = gridWrapper.current
      const verticalScrollBoxWidth = offsetWidth - clientWidth
      setGridWidth(Math.round(gridRef.current.offsetWidth + verticalScrollBoxWidth))
    }
  }, [gridRef])

  const onSyncScroll = (event: Event) => {
    if (gridWrapper.current) {
      gridWrapper.current.scrollLeft = (event.target as HTMLDivElement).scrollLeft
    }
  }

  useEffect(() => {
    const resizeSubscription: Subscription = fromEvent(window, "resize").pipe(debounceTime(50)).subscribe(updateWidth)

    const scrollSubscription: Subscription = fromEvent(scrollWrapper.current!, "scroll")
      .pipe(debounceTime(5))
      .subscribe(onSyncScroll)

    updateWidth()

    return () => {
      resizeSubscription.unsubscribe()
      scrollSubscription.unsubscribe()
    }
  }, [updateWidth])

  useEffect(() => {
    if (recalculateWidth) {
      updateWidth()
    } else {
      setGridWidth(0)
    }
  }, [recalculateWidth, updateWidth])

  const visibleHorizontalScroll = () => {
    if (gridWrapper.current) {
      const { scrollWidth, clientWidth } = gridWrapper.current
      return visible && gridWidth > 0 && scrollWidth > clientWidth
    }
    return false
  }

  return (
    <>
      <div
        ref={scrollWrapper}
        className="scroll-wrapper"
        style={{ visibility: visibleHorizontalScroll() ? "visible" : "hidden" }}
      >
        <div style={{ height: "1px", width: `${gridWidth}px` ?? "100%" }} />
      </div>
      <div
        className="table-sticky-head overflow-x-hide mb-2"
        ref={gridWrapper}
        style={{ height: `calc(100vh - ${offsetVertical}px)` }}
      >
        {children}
      </div>
    </>
  )
}

HorizontalScrollWrapper.defaultProps = {
  visible: true,
  offsetVertical: 0,
}

export default HorizontalScrollWrapper
